/**
 * TEMPORARY: Firebase to Supabase migration modal
 * @see MIGRATION-REMOVAL.md for removal instructions
 *
 * This modal prompts users who have an active Firebase session to enter their
 * password to complete the migration to Supabase authentication. This allows
 * them to continue using the app seamlessly without losing their current session.
 */

import { useQueryClient } from '@tanstack/react-query';
import type { User as FirebaseUser } from 'firebase/auth';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useCallback, useState } from 'react';

import { BoxedIconMessage } from '@/components/BoxedIconMessage';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { PasswordIcon } from '@/icons';
import { getFirebaseAuth } from '@/lib/firebase';
import { supabase } from '@/lib/supabase';

interface MigrationModalProps {
	isOpen: boolean;
	firebaseUser: FirebaseUser | null;
	onMigrationComplete: () => void;
}

const MigrationModal = ({ isOpen, firebaseUser, onMigrationComplete }: MigrationModalProps) => {
	const queryClient = useQueryClient();
	const [password, setPassword] = useState('');
	const [isPasswordVisible, setIsPasswordVisible] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleMigration = useCallback(async () => {
		if (!firebaseUser?.email || !password) {
			setError('Please enter your password');
			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			// Re-authenticate with Firebase to verify password is correct
			const auth = getFirebaseAuth();
			await signInWithEmailAndPassword(auth, firebaseUser.email, password);

			// Now migrate to Supabase via Edge Function
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/migrate-password`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
					},
					body: JSON.stringify({
						email: firebaseUser.email,
						password,
						firebase_uid: firebaseUser.uid,
						school_id: import.meta.env.VITE_SCHOOL_ID,
					}),
				}
			);

			const result = await response.json();

			if (result.success) {
				// Password migrated! Now sign in with Supabase
				const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
					email: firebaseUser.email,
					password,
				});

				if (signInError) {
					throw new Error(signInError.message);
				}

				if (signInData?.user) {
					// Invalidate queries to refetch with new auth state
					await queryClient.invalidateQueries();

					// Wait for auth state to propagate
					await new Promise<void>((resolve) => {
						setTimeout(resolve, 100);
					});

					// Sign out of Firebase since we're now on Supabase
					await auth.signOut();

					onMigrationComplete();
				}
			} else {
				throw new Error(result.error || 'Migration failed. Please try again.');
			}
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : 'Something went wrong. Please try again.';

			// Handle Firebase-specific error codes
			if (errorMessage.includes('auth/wrong-password')) {
				setError('Incorrect password. Please try again.');
			} else if (errorMessage.includes('auth/too-many-requests')) {
				setError('Too many attempts. Please try again later.');
			} else {
				setError(errorMessage);
			}
		} finally {
			setIsSubmitting(false);
		}
	}, [firebaseUser, password, queryClient, onMigrationComplete]);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !isSubmitting) {
			handleMigration();
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={() => {}}>
			<DialogContent
				className="max-w-[450px] [&>button:last-child]:hidden"
				onInteractOutside={(e) => e.preventDefault()}
				onEscapeKeyDown={(e) => e.preventDefault()}
			>
				<DialogHeader>
					<DialogTitle className="pb-2">Update Required</DialogTitle>
					<DialogDescription className="text-base">
						We've upgraded our authentication system for better security. Please enter your password
						to continue.
					</DialogDescription>
				</DialogHeader>

				<p className="text-base text-muted-foreground pb-2">
					Logged in as: <strong className="text-foreground">{firebaseUser?.email}</strong>
				</p>

				<div>
					<label htmlFor="migration-password" className="block mb-2 text-sm">
						Password
					</label>
					<div className="relative">
						<Input
							id="migration-password"
							type={isPasswordVisible ? 'text' : 'password'}
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							onKeyDown={handleKeyDown}
							className="mb-3"
							autoFocus
						/>
						<button
							type="button"
							aria-label="Toggle Password Visibility"
							className="absolute right-0 top-0 bottom-0 px-1 w-auto flex items-center max-h-10"
							onClick={() => setIsPasswordVisible(!isPasswordVisible)}
						>
							<PasswordIcon isPasswordVisible={isPasswordVisible} />
						</button>
					</div>
				</div>

				{error && <BoxedIconMessage message={error} messageColor="var(--red-200)" hasError />}

				<div className="flex gap-3">
					<Button
						type="button"
						onClick={handleMigration}
						isLoading={isSubmitting}
						disabled={isSubmitting || !password}
					>
						{isSubmitting ? 'Updating...' : 'Continue'}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default MigrationModal;
